import { useEffect, useRef, useState, type FormEvent, type MouseEvent } from "react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Field";
import { useT, type UiLang } from "../lib/i18n";
import { AuthLayout, DiscordButton, Spinner } from "../components/shell/AuthLayout";
import { loginWithOneTimeToken, loginWithPassword, type AuthLostReason } from "../lib/api";
import { loginSession } from "../lib/session";
import { landingUrl, markJustLoggedIn, stashLandingHash, peekLandingHash } from "../lib/landing";
import { superAdminEntry } from "../lib/super-admin";
import { discordConfigured, discordEntry } from "../lib/discord";
import { handOff, openPopup, startWaiting } from "../lib/discord-popup";

/**
 * 未登录门禁。
 *
 * **不渲染任何配置界面。** 之前没凭据时也照常显示整个后台，数据来自前端造的
 * 假数据——两个问题：菜单、字段、业务规则提示对任何人可见；而且假数据和真配置
 * 看不出区别，有人会当成线上数值去讨论。
 *
 * 两条进门的路（leo 2026-09-11：管端做成独立登录，不依赖中台）：
 *   1. 管端自有账号：这里直接填用户名密码，POST /admin/api/login 换票；
 *   2. 从 super-admin 跳进来：带中台 token，走换票。保留，按钮在下面。
 *   3. Discord（2026-09-29 会议定的主路径，10-19 起是登录页的主按钮）：notify-hub 上授权完，把我们重定向回
 *      `?token=<一次性 token>`，这一页开屏就把它换成管端票。账号名单在 notify-hub 侧统一管，
 *      所以这条路不需要我们先建账号；上面那两条保留——没绑过 Discord 的人要靠它们进来做第一次绑定，
 *      进来之后没绑就只能看到绑定页（DiscordBindRequired），用不了系统。
 *      10-22 起点按钮是开小窗授权，这一页留着等小窗交回 token（lib/discord-popup.ts）；小窗被拦才整页跳过去。
 * 也**不自动跳转**：跳走的话人看不清为什么被弹开，本地调试也没法停在这一页。
 */
/** 为什么回到登录页：过期 / 被服务端拒（带原话）/ 别的标签页退出了 / 别的标签页换了账号。null = 本来就没登录 */
export type GateLost = AuthLostReason | { kind: "elsewhere" } | { kind: "switched" };

export function Gate({
  lost, uiLang, onUiLang,
}: {
  lost: GateLost | null;
  uiLang: UiLang; onUiLang: (l: UiLang) => void;
}) {
  const t = useT();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const [err, setErr] = useState<string | null>(null);
  /*
   * Discord 回跳：notify-hub 把人送回 `…/discord/callback?token=xxx`。
   * **换完立刻把 token 从地址栏抹掉**（replaceState）——它是一次性的，留在历史记录里
   * 只会被分享出去或被重放；换失败也要抹，否则刷新一次又拿同一枚坏 token 再试一遍。
   */
  const [exchanging, setExchanging] = useState(() => new URLSearchParams(window.location.search).has("token"));
  /** 这是登录小窗，token 已交回原来的登录页：只剩一句「可以关掉」（window.close 不一定关得掉） */
  const [handedOff, setHandedOff] = useState(false);
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const oneTime = qs.get("token");
    if (!oneTime) return;
    let alive = true;
    void (async () => {
      // 先抹掉地址栏里的一次性 token，交给原页面还是自己换都一样要抹
      history.replaceState(null, "", window.location.pathname + window.location.hash);
      if (await handOff(oneTime)) {
        // 不看 alive：开发模式 StrictMode 第二遍 effect 看到的地址栏已经没有 token，这一遍的结果必须落地
        setHandedOff(true);
        window.close();
        return;
      }
      try {
        const r = await loginWithOneTimeToken(oneTime);
        loginSession(r.token);
        markJustLoggedIn();
        // 先无导航地改地址（收回回跳路径），再只重进一次；理由见 lib/landing.ts
        history.replaceState(null, "", landingUrl(window.location, "discord", peekLandingHash()));
        window.location.reload();
      } catch (e) {
        if (!alive) return;
        setErr((e as Error).message || t("gate.discordFailed"));
        setExchanging(false);
      }
    })();
    return () => { alive = false; };
    // 只在挂载时跑一次：地址栏里的 token 是一次性的，重复跑等于拿已消费的票再换一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * 一打开登录页就记下当前 hash，不等点按钮：人也可能去 Discord 私信里点登录链接回来，
   * 那条路不经过我们的按钮，回跳地址也不带 hash，不提前记就只能落回首页。
   * 回跳页（exchanging）本身没有 hash，这里不碰，免得把刚才记的清掉。
   */
  useEffect(() => {
    if (!exchanging && window.location.hash.length > 1) stashLandingHash(window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /*
   * 小窗授权：等待期间这一页只显示「去小窗里授权」。小窗交回 token 后在这里换票，
   * 落地地址按 password 的口径留在当前路径与 hash 上——这一页从没离开过，不需要回跳那套。
   */
  const [waiting, setWaiting] = useState<{ stop: () => void } | null>(null);
  useEffect(() => () => waiting?.stop(), [waiting]);
  const exchangeFromPopup = async (oneTime: string) => {
    window.focus();
    setWaiting(null);
    setExchanging(true);
    try {
      const r = await loginWithOneTimeToken(oneTime);
      loginSession(r.token);
      markJustLoggedIn();
      history.replaceState(null, "", landingUrl(window.location, "password"));
      window.location.reload();
    } catch (e) {
      setErr((e as Error).message || t("gate.discordFailed"));
      setExchanging(false);
    }
  };
  const onDiscordClick = (e: MouseEvent<HTMLAnchorElement>) => {
    stashLandingHash(window.location.hash);
    // 中键、Ctrl/⌘ 点开新标签页的照旧，那是人自己选的整页跳转
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    setErr(null);
    const w = startWaiting((tok) => void exchangeFromPopup(tok));
    if (!openPopup(discordEntry())) {
      // 浏览器拦了小窗：退回原来的整页跳转
      w.stop();
      window.location.assign(discordEntry());
      return;
    }
    setWaiting(w);
  };

  // 点击和回车都交给表单提交；同步锁也挡住同一帧内的重复提交。
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting.current || exchanging) return;
    if (!username.trim() || !password) { setErr(t("gate.loginMissing")); return; }
    submitting.current = true;
    setBusy(true); setErr(null);
    try {
      const r = await loginWithPassword(username.trim(), password);
      loginSession(r.token);
      markJustLoggedIn();
      // 会话在 App 挂载时读一次（bootSession），落盘后整页重进最简单也最不容易漏状态。
      // 只导航一次：先无导航地整理地址，再 reload；理由见 lib/landing.ts
      history.replaceState(null, "", landingUrl(window.location, "password"));
      window.location.reload();
    } catch (e2) {
      submitting.current = false;
      setErr((e2 as Error).message || t("gate.loginFailed"));
      setBusy(false);
    }
  };

  const discord = discordConfigured();
  /*
   * 账号密码收在「其他登录方式」里（leo 2026-10-19：Discord 是主登录）。但**不能删**：
   * 没绑过 Discord 的人得先用它登进来做第一次绑定（见 DiscordBindRequired）。
   * 没接 Discord 的环境（正式环境 notify-hub 还没配）它直接就是主入口，不用点展开。
   */
  const [showPassword, setShowPassword] = useState(!discord);
  const passwordForm = (
    <form onSubmit={submit} className="space-y-3">
      <Input value={username} onChange={setUsername} placeholder={t("gate.username")} autoComplete="username" disabled={busy} className="!h-10 !rounded-lg !px-3" autoFocus />
      <Input value={password} onChange={setPassword} placeholder={t("gate.password")} type="password" autoComplete="current-password" disabled={busy} className="!h-10 !rounded-lg !px-3" />
      <Button type="submit" tone={discord ? "default" : "primary"} className="!h-10 w-full !rounded-lg" disabled={busy || exchanging}>
        {busy ? t("gate.loggingIn") : t("gate.login")}
      </Button>
    </form>
  );

  return (
    <AuthLayout uiLang={uiLang} onUiLang={onUiLang}>
      {handedOff ? (
        <div className="flex flex-col items-center py-8 text-center">
          <p className="text-base font-medium text-ink">{t("gate.popupDone")}</p>
          <p className="mt-2 text-sm text-ink-secondary">{t("gate.popupDoneHint")}</p>
        </div>
      ) : waiting ? (
        <div className="flex flex-col items-center py-8 text-center">
          <span className="text-[#5865F2]"><Spinner className="h-8 w-8" /></span>
          <p className="mt-4 text-base font-medium text-ink">{t("gate.popupWaiting")}</p>
          <p className="mt-2 text-sm leading-relaxed text-ink-secondary">{t("gate.popupWaitingHint")}</p>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => { if (!openPopup(discordEntry())) setErr(t("gate.popupBlocked")); }}>{t("gate.popupReopen")}</Button>
            <Button onClick={() => setWaiting(null)}>{t("gate.popupCancel")}</Button>
          </div>
          {err && <p className="mt-4 rounded-lg bg-danger-50 px-3 py-2 text-sm leading-relaxed text-danger-800">{err}</p>}
        </div>
      ) : exchanging ? (
        // 从 Discord 回来、正在换票：只留一个状态，别让人在这几百毫秒里去点别的
        <div className="flex flex-col items-center py-8 text-center">
          <span className="text-[#5865F2]"><Spinner className="h-8 w-8" /></span>
          <p className="mt-4 text-base font-medium text-ink">{t("gate.discordExchanging")}</p>
        </div>
      ) : (
        <>
          <h1 className="text-xl font-semibold text-ink">{t("gate.loginTitle")}</h1>
          <p className="mt-1.5 text-sm leading-relaxed text-ink-secondary">{discord ? t("gate.subtitleDiscord") : t("gate.subtitle")}</p>
          {lost && (
            <p className="mt-4 rounded-lg bg-warning-50 px-3 py-2 text-sm leading-relaxed text-warning-800">
              {lost.kind === "expired" ? t("gate.expired")
                : lost.kind === "elsewhere" ? t("gate.loggedOutElsewhere")
                  : lost.kind === "switched" ? t("gate.switchedElsewhere")
                    : t("gate.rejected", { msg: lost.message })}
            </p>
          )}
          {err && <p className="mt-4 rounded-lg bg-danger-50 px-3 py-2 text-sm leading-relaxed text-danger-800">{err}</p>}

          {discord && (
            <div className="mt-6">
              <DiscordButton href={discordEntry()} onClick={onDiscordClick} busy={busy}>
                {t("gate.discordLogin")}
              </DiscordButton>
              <p className="mt-3 text-xs leading-relaxed text-ink-placeholder">{t("gate.discordHint")}</p>
            </div>
          )}

          {discord && (
            <div className="my-6 flex items-center gap-3 text-xs text-ink-placeholder">
              <span className="h-px flex-1 bg-line-lighter" />{t("gate.otherWays")}<span className="h-px flex-1 bg-line-lighter" />
            </div>
          )}

          <div className={discord ? "" : "mt-6"}>
            {showPassword ? passwordForm : (
              <button type="button" onClick={() => setShowPassword(true)}
                className="flex h-10 w-full items-center justify-center rounded-lg border border-line text-base text-ink-regular transition-colors hover:border-primary hover:text-primary">
                {t("gate.usePassword")}
              </button>
            )}
          </div>

          <p className="mt-5 text-center text-xs text-ink-placeholder">
            {t("gate.fromSuperAdmin")}
            <a href={superAdminEntry()} onClick={() => stashLandingHash(window.location.hash)} className="ml-1 text-primary hover:underline">
              {t("gate.enter")}
            </a>
          </p>
        </>
      )}
    </AuthLayout>
  );
}
